/**
 * Plan limit helpers.
 * Convention: limit < 0 (e.g. -1) = unlimited, 0 = none allowed, >0 = max count.
 */

export const isUnlimitedPlanLimit = (limit) => {
  const n = parseInt(limit, 10);
  return !Number.isNaN(n) && n < 0;
};

export const isAtPlanLimit = (limit, used) => {
  if (isUnlimitedPlanLimit(limit)) {
    return false;
  }
  const max = parseInt(limit, 10);
  const count = parseInt(used, 10) || 0;
  if (Number.isNaN(max)) {
    return false;
  }
  return count >= max;
};

export const PLAN_LIMIT_TYPES = {
  EVENTS: "events",
  SEATS: "seats",
  CALENDARS: "calendars",
  ROUND_ROBIN: "round_robin",
  STAFF_CONFIRM: "staff_confirm",
};

export const PLAN_LIMIT_LABELS = {
  events: "events",
  seats: "seats",
  calendars: "calendars",
  round_robin: "round robin events",
  staff_confirm: "staff confirmation events",
};

export const formatRoundRobinLimit = (limit, featureEnabled = true) => {
  if (!featureEnabled) {
    return "Not included";
  }
  if (limit === null || limit === undefined || limit === "") {
    return "Unlimited";
  }
  if (isUnlimitedPlanLimit(limit)) {
    return "Unlimited";
  }
  const n = parseInt(limit, 10);
  if (Number.isNaN(n) || n <= 0) {
    return "None";
  }
  return n === 1 ? "1 event" : `${n} events`;
};

export const formatStaffConfirmLimit = formatRoundRobinLimit;

/**
 * Price shown on plan cards. Annual billing shows monthly equivalent (annual ÷ 12).
 * Checkout / Stripe charges must still use the full annual_price.
 */
export const getPlanDisplayPrice = (plan, period = "monthly") => {
  if (!plan) return 0;
  if (period === "monthly") {
    return Number(plan.monthly_price != null ? plan.monthly_price : 0);
  }
  const annual = Number(plan.annual_price != null ? plan.annual_price : 0);
  if (annual === 0) return 0;
  const monthlyEquivalent = annual / 12;
  return Number.isInteger(monthlyEquivalent)
    ? monthlyEquivalent
    : Math.round(monthlyEquivalent * 100) / 100;
};

/** Calendar policy copy — matches public /pricing (admin vs staff allotment). */
export const CALENDAR_STAFF_REQUEST_HINT =
  "Additional staff calendar connections are available by approved request.";

/**
 * Connected calendars label from plan.no_of_calendars.
 * Free / 1 calendar → admin-only; paid → Admin: N · Staff: 1 each.
 */
export const getPlanCalendarText = (plan, { short = false } = {}) => {
  if (!plan) return "—";
  const n = parseInt(plan.no_of_calendars, 10);
  if (Number.isNaN(n)) return "—";
  if (n < 0) return short ? "Unlimited" : "Unlimited calendars";
  const isFree = Number(plan.monthly_price) === 0;
  if (isFree || n === 1) return "1 admin calendar";
  return `Admin: ${n} · Staff: 1 each`;
};

/** Event limit label from plan.no_of_events. */
export const getPlanEventsText = (plan, { short = false } = {}) => {
  if (!plan) return "—";
  const n = parseInt(plan.no_of_events, 10);
  if (Number.isNaN(n)) return "—";
  if (n < 0) return short ? "Unlimited" : "Unlimited events";
  if (n === 1) return short ? "1 only" : "1 event only";
  return short ? `${n}` : `${n} events`;
};
